import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalApi } from '../../lib/parceiroApi';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ChevronLeft, ChevronRight, Loader2, Camera, X, CheckCircle2, Building2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const PASSOS = ['Local', 'Cliente', 'Serviço', 'Anexos', 'Prioridade', 'Data', 'Revisão'];
const emptyEndereco = { cep: '', rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', zona: '' };

export default function ParceiroNovaSolicitacao() {
  const navigate = useNavigate();
  const [passo, setPasso] = useState(0);
  const [imoveis, setImoveis] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ok, setOk] = useState(null);

  const [form, setForm] = useState({
    imovel_id: '', usar_novo: false,
    novo_imovel: { tipo: 'imovel', codigo_interno: '', condominio: '', bloco: '', unidade: '', morador: '', endereco: { ...emptyEndereco } },
    cliente_nome: '', cliente_whatsapp: '', cliente_telefone: '', cliente_email: '',
    categoria: '', itens: [], descricao: '', anexos: [],
    prioridade: 'normal', data_desejada: '', periodo: '',
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    portalApi.listImoveis().then((r) => setImoveis(r.data)).catch(() => {});
    portalApi.publicServicos().then((r) => setServicos(r.data)).catch(() => {});
    portalApi.publicCidades().then((r) => setCidades(r.data)).catch(() => {});
  }, []);

  const categorias = useMemo(() => [...new Set(servicos.map((s) => s.categoria_nome).filter(Boolean))], [servicos]);
  const servicosFiltrados = useMemo(
    () => (form.categoria ? servicos.filter((s) => s.categoria_nome === form.categoria) : servicos),
    [servicos, form.categoria]
  );
  const imovelSel = imoveis.find((i) => i.id === form.imovel_id);
  const cidadeSel = cidades.find((c) => c.nome === form.novo_imovel.endereco.cidade && c.estado === form.novo_imovel.endereco.estado);
  const totalServicos = form.itens.reduce((s, it) => s + it.preco * it.quantidade, 0);

  const addServico = (s) => {
    if (form.itens.some((it) => it.servico_id === s.id)) return;
    set({ itens: [...form.itens, { servico_id: s.id, nome: s.nome, preco: parseFloat(s.preco_base || 0), quantidade: 1 }] });
  };
  const setQtd = (sid, delta) => {
    set({
      itens: form.itens
        .map((it) => (it.servico_id === sid ? { ...it, quantidade: it.quantidade + delta } : it))
        .filter((it) => it.quantidade > 0),
    });
  };

  const podeAvancar = () => {
    if (passo === 0) return form.usar_novo ? !!(form.novo_imovel.endereco.rua && form.novo_imovel.endereco.cidade) : !!form.imovel_id;
    if (passo === 1) return !!(form.cliente_nome && (form.cliente_whatsapp || form.cliente_telefone));
    if (passo === 2) return form.itens.length > 0;
    if (passo === 5) return !!form.data_desejada;
    return true;
  };

  const selecionarImovel = (im) => {
    set({ imovel_id: im.id, usar_novo: false, cliente_nome: form.cliente_nome || im.morador || '', cliente_whatsapp: form.cliente_whatsapp || im.whatsapp || im.telefone || '' });
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const r = await portalApi.upload(files);
      set({ anexos: [...form.anexos, ...r.data] });
      toast.success(`${r.data.length} arquivo(s) anexado(s)`);
    } catch (err) {
      toast.error('Falha no upload. Tente arquivos menores (máx. 20MB).');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const enviar = async () => {
    setEnviando(true);
    try {
      const payload = {
        imovel_id: form.usar_novo ? null : form.imovel_id,
        novo_imovel: form.usar_novo ? form.novo_imovel : null,
        cliente_nome: form.cliente_nome,
        cliente_whatsapp: form.cliente_whatsapp,
        cliente_telefone: form.cliente_telefone,
        cliente_email: form.cliente_email,
        servico_id: null,
        itens: form.itens.map((it) => ({ servico_id: it.servico_id, quantidade: it.quantidade })),
        descricao: form.descricao,
        anexos: form.anexos,
        prioridade: form.prioridade,
        data_desejada: form.data_desejada,
        periodo: form.periodo,
      };
      const r = await portalApi.createSolicitacao(payload);
      setOk(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao enviar solicitação');
    } finally {
      setEnviando(false);
    }
  };

  if (ok) {
    return (
      <div className="max-w-md mx-auto text-center py-10" data-testid="solicitacao-sucesso">
        <CheckCircle2 size={56} className="text-[#2B7A3D] mx-auto mb-4" />
        <h1 className="text-xl font-bold text-[#3D4F5F]">Solicitação enviada!</h1>
        <p className="text-sm text-gray-600 mt-2">
          OS <b>#OS-{String(ok.numero).padStart(4, '0')}</b> criada{ok.valor > 0 ? <> no valor de <b className="text-[#2B7A3D]">{formatCurrency(ok.valor)}</b></> : ''}. A data informada é uma preferência — a Tenance confirmará o agendamento.
        </p>
        {ok.valor > 0 && <p className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2 mt-3">Falta só um passo: <b>aprove o valor</b> para autorizar a execução.</p>}
        <div className="flex gap-2 justify-center mt-5">
          <Button variant="outline" onClick={() => navigate('/parceiro/dashboard')}>Início</Button>
          <Button className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => navigate(`/parceiro/solicitacoes/${ok.id}`)} data-testid="btn-ver-solicitacao">
            {ok.valor > 0 ? 'Aprovar valor agora' : 'Ver solicitação'}
          </Button>
        </div>
      </div>
    );
  }

  const ne = form.novo_imovel;
  const setNE = (patch) => set({ novo_imovel: { ...ne, ...patch } });
  const setNEEnd = (patch) => setNE({ endereco: { ...ne.endereco, ...patch } });

  return (
    <div className="max-w-xl mx-auto" data-testid="nova-solicitacao">
      {/* Stepper */}
      <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1">
        {PASSOS.map((p, i) => (
          <div key={p} className="flex items-center gap-1 shrink-0">
            <div className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center ${i <= passo ? 'bg-[#2B7A3D] text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</div>
            <span className={`text-[11px] ${i === passo ? 'font-semibold text-[#3D4F5F]' : 'text-gray-400'} hidden sm:block`}>{p}</span>
            {i < PASSOS.length - 1 && <div className="w-3 h-px bg-gray-300" />}
          </div>
        ))}
      </div>
      <h1 className="text-lg font-bold text-[#3D4F5F] mb-3">{passo + 1}. {PASSOS[passo]}</h1>

      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        {passo === 0 && (
          <div className="space-y-3">
            {!form.usar_novo && (
              <>
                {imoveis.length === 0 && <p className="text-sm text-gray-500">Você ainda não tem imóveis cadastrados.</p>}
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {imoveis.map((im) => (
                    <button key={im.id} onClick={() => selecionarImovel(im)} data-testid={`imovel-opcao-${im.id}`}
                      className={`w-full text-left rounded-lg border p-3 text-sm transition-colors ${form.imovel_id === im.id ? 'border-[#2B7A3D] bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className="flex items-center gap-2 font-semibold text-[#3D4F5F]"><Building2 size={15} />
                        {im.condominio ? `${im.condominio} ${im.bloco ? '· ' + im.bloco : ''} ${im.unidade ? '· ' + im.unidade : ''}` : `${im.endereco?.rua || ''}, ${im.endereco?.numero || ''}`}
                        {im.codigo_interno && <span className="text-xs text-gray-400">({im.codigo_interno})</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{im.endereco?.bairro} · {im.endereco?.cidade}{im.endereco?.zona ? ` · ${im.endereco.zona}` : ''} {im.morador ? `· Morador: ${im.morador}` : ''}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
            <Button variant={form.usar_novo ? 'default' : 'outline'} className={form.usar_novo ? 'bg-[#3D4F5F]' : ''} onClick={() => set({ usar_novo: !form.usar_novo, imovel_id: '' })} data-testid="btn-novo-imovel">
              <Plus size={16} className="mr-1" /> {form.usar_novo ? 'Voltar para meus imóveis' : 'Novo imóvel / endereço'}
            </Button>
            {form.usar_novo && (
              <div className="space-y-3 pt-1">
                <div className="flex gap-2">
                  {['imovel', 'condominio'].map((t) => (
                    <button key={t} onClick={() => setNE({ tipo: t })} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${ne.tipo === t ? 'bg-[#2B7A3D] text-white border-[#2B7A3D]' : 'border-gray-300 text-gray-600'}`}>
                      {t === 'imovel' ? 'Imóvel' : 'Condomínio'}
                    </button>
                  ))}
                </div>
                {ne.tipo === 'condominio' && (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-3 sm:col-span-1"><Label>Condomínio</Label><Input value={ne.condominio} onChange={(e) => setNE({ condominio: e.target.value })} /></div>
                    <div><Label>Bloco</Label><Input value={ne.bloco} onChange={(e) => setNE({ bloco: e.target.value })} /></div>
                    <div><Label>Unidade</Label><Input value={ne.unidade} onChange={(e) => setNE({ unidade: e.target.value })} /></div>
                  </div>
                )}
                <div><Label>Código interno (opcional)</Label><Input value={ne.codigo_interno} onChange={(e) => setNE({ codigo_interno: e.target.value })} placeholder="ex: IM-1023" /></div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-3"><Label>Rua *</Label><Input value={ne.endereco.rua} onChange={(e) => setNEEnd({ rua: e.target.value })} data-testid="novo-imovel-rua" /></div>
                  <div><Label>Nº</Label><Input value={ne.endereco.numero} onChange={(e) => setNEEnd({ numero: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Complemento</Label><Input value={ne.endereco.complemento} onChange={(e) => setNEEnd({ complemento: e.target.value })} /></div>
                  <div><Label>Bairro</Label><Input value={ne.endereco.bairro} onChange={(e) => setNEEnd({ bairro: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Cidade *</Label>
                    <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={`${ne.endereco.cidade}|${ne.endereco.estado}`}
                      onChange={(e) => { const [cid, uf] = e.target.value.split('|'); setNEEnd({ cidade: cid || '', estado: uf || '', zona: '' }); }} data-testid="novo-imovel-cidade">
                      <option value="|">Selecione...</option>
                      {cidades.map((c) => <option key={c.id} value={`${c.nome}|${c.estado}`}>{`${c.nome}/${c.estado}`}</option>)}
                    </select>
                  </div>
                  {cidadeSel?.zonas?.length > 0 && (
                    <div>
                      <Label>Zona/Região</Label>
                      <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={ne.endereco.zona} onChange={(e) => setNEEnd({ zona: e.target.value })}>
                        <option value="">Selecione...</option>
                        {cidadeSel.zonas.map((z) => <option key={z.nome} value={z.nome}>{z.nome}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>CEP</Label><Input value={ne.endereco.cep} onChange={(e) => setNEEnd({ cep: e.target.value })} /></div>
                  <div><Label>Morador</Label><Input value={ne.morador} onChange={(e) => setNE({ morador: e.target.value })} /></div>
                </div>
              </div>
            )}
          </div>
        )}

        {passo === 1 && (
          <div className="space-y-3">
            <div><Label>Nome do cliente/morador *</Label><Input value={form.cliente_nome} onChange={(e) => set({ cliente_nome: e.target.value })} data-testid="cliente-nome" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>WhatsApp *</Label><Input value={form.cliente_whatsapp} onChange={(e) => set({ cliente_whatsapp: e.target.value })} placeholder="(11) 99999-9999" data-testid="cliente-whatsapp" /></div>
              <div><Label>Telefone</Label><Input value={form.cliente_telefone} onChange={(e) => set({ cliente_telefone: e.target.value })} /></div>
            </div>
            <div><Label>E-mail (opcional)</Label><Input type="email" value={form.cliente_email} onChange={(e) => set({ cliente_email: e.target.value })} /></div>
          </div>
        )}

        {passo === 2 && (
          <div className="space-y-3">
            <div>
              <Label>Categoria</Label>
              <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.categoria} onChange={(e) => set({ categoria: e.target.value })} data-testid="select-categoria">
                <option value="">Todas</option>
                {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Serviços * (toque para adicionar)</Label>
              <div className="space-y-1.5 max-h-56 overflow-y-auto mt-1">
                {servicosFiltrados.map((s) => {
                  const adicionado = form.itens.some((it) => it.servico_id === s.id);
                  return (
                    <button key={s.id} onClick={() => addServico(s)} disabled={adicionado} data-testid={`servico-opcao-${s.id}`}
                      className={`w-full flex items-center justify-between gap-2 rounded-lg border p-2.5 text-sm text-left ${adicionado ? 'border-[#2B7A3D] bg-green-50 opacity-60' : 'border-gray-200 hover:border-[#2B7A3D]'}`}>
                      <span className="truncate">{s.nome}</span>
                      <span className="font-semibold text-[#2B7A3D] shrink-0">{formatCurrency(parseFloat(s.preco_base || 0))}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {form.itens.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-3 space-y-2" data-testid="itens-selecionados">
                <div className="text-xs font-semibold text-[#3D4F5F]">Serviços selecionados</div>
                {form.itens.map((it) => (
                  <div key={it.servico_id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{it.nome}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setQtd(it.servico_id, -1)} className="w-7 h-7 rounded-full border border-gray-300 font-bold text-gray-600" data-testid={`qtd-menos-${it.servico_id}`}>−</button>
                      <span className="w-5 text-center font-semibold">{it.quantidade}</span>
                      <button onClick={() => setQtd(it.servico_id, 1)} className="w-7 h-7 rounded-full border border-gray-300 font-bold text-gray-600" data-testid={`qtd-mais-${it.servico_id}`}>+</button>
                      <span className="font-semibold w-20 text-right">{formatCurrency(it.preco * it.quantidade)}</span>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between items-center border-t border-gray-200 pt-2 font-bold text-[#3D4F5F]">
                  <span>Total</span>
                  <span className="text-[#2B7A3D] text-lg" data-testid="total-servicos">{formatCurrency(totalServicos)}</span>
                </div>
              </div>
            )}
            <div>
              <Label>Descreva o problema</Label>
              <Textarea rows={3} value={form.descricao} onChange={(e) => set({ descricao: e.target.value })} placeholder="ex: Vazamento embaixo da pia da cozinha..." data-testid="descricao-problema" />
            </div>
          </div>
        )}

        {passo === 3 && (
          <div className="space-y-3">
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-[#2B7A3D] text-gray-500">
              {uploading ? <Loader2 className="animate-spin" /> : <Camera size={28} />}
              <span className="text-sm mt-2">{uploading ? 'Enviando...' : 'Toque para anexar fotos, vídeos ou documentos'}</span>
              <input type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx" className="hidden" onChange={handleUpload} data-testid="input-anexos" disabled={uploading} />
            </label>
            {form.anexos.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {form.anexos.map((a, i) => (
                  <div key={a.file_id} className="relative rounded-lg border border-gray-200 overflow-hidden">
                    {a.content_type?.startsWith('image/')
                      ? <img src={a.url} alt={a.nome} className="h-20 w-full object-cover" />
                      : <div className="h-20 flex items-center justify-center text-[10px] text-gray-500 px-1 text-center">{a.nome}</div>}
                    <button onClick={() => set({ anexos: form.anexos.filter((_, j) => j !== i) })} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400">Anexos são opcionais, mas ajudam no orçamento.</p>
          </div>
        )}

        {passo === 4 && (
          <div className="grid grid-cols-2 gap-3">
            {[{ v: 'normal', t: 'Normal', d: 'Agendamento padrão' }, { v: 'urgente', t: 'Urgente', d: 'Precisa de atendimento rápido' }].map((p) => (
              <button key={p.v} onClick={() => set({ prioridade: p.v })} data-testid={`prioridade-${p.v}`}
                className={`rounded-xl border p-4 text-left ${form.prioridade === p.v ? (p.v === 'urgente' ? 'border-red-500 bg-red-50' : 'border-[#2B7A3D] bg-green-50') : 'border-gray-200'}`}>
                <div className="font-semibold text-sm">{p.t}</div>
                <div className="text-xs text-gray-500 mt-1">{p.d}</div>
              </button>
            ))}
          </div>
        )}

        {passo === 5 && (
          <div className="space-y-3">
            <div><Label>Data desejada *</Label><Input type="date" value={form.data_desejada} onChange={(e) => set({ data_desejada: e.target.value })} data-testid="data-desejada" /></div>
            <div>
              <Label>Período</Label>
              <div className="flex gap-2 mt-1">
                {['Manhã', 'Tarde', 'Noite'].map((p) => (
                  <button key={p} onClick={() => set({ periodo: form.periodo === p ? '' : p })} data-testid={`periodo-${p}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium border ${form.periodo === p ? 'bg-[#2B7A3D] text-white border-[#2B7A3D]' : 'border-gray-300 text-gray-600'}`}>{p}</button>
                ))}
              </div>
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2">A data é uma preferência — a Tenance confirmará o agendamento.</p>
          </div>
        )}

        {passo === 6 && (
          <div className="space-y-2 text-sm" data-testid="revisao-solicitacao">
            <Resumo label="Local" valor={form.usar_novo
              ? `${ne.condominio ? ne.condominio + ' · ' : ''}${ne.endereco.rua}, ${ne.endereco.numero} - ${ne.endereco.cidade}`
              : (imovelSel ? (imovelSel.condominio ? `${imovelSel.condominio} ${imovelSel.unidade || ''}` : `${imovelSel.endereco?.rua || ''}, ${imovelSel.endereco?.numero || ''}`) : '')} />
            <Resumo label="Cliente" valor={`${form.cliente_nome} · ${form.cliente_whatsapp || form.cliente_telefone}`} />
            <div className="border border-gray-200 rounded-lg p-2.5 space-y-1">
              <div className="text-xs font-semibold text-gray-500">Serviços</div>
              {form.itens.map((it) => (
                <div key={it.servico_id} className="flex justify-between gap-2">
                  <span>{it.nome}{it.quantidade > 1 ? ` (x${it.quantidade})` : ''}</span>
                  <span className="font-medium">{formatCurrency(it.preco * it.quantidade)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-gray-100 pt-1.5 font-bold text-[#3D4F5F]">
                <span>TOTAL</span>
                <span className="text-[#2B7A3D]" data-testid="total-revisao">{formatCurrency(totalServicos)}</span>
              </div>
            </div>
            {form.descricao && <Resumo label="Descrição" valor={form.descricao} />}
            <Resumo label="Anexos" valor={`${form.anexos.length} arquivo(s)`} />
            <Resumo label="Prioridade" valor={form.prioridade === 'urgente' ? 'Urgente' : 'Normal'} />
            <Resumo label="Data desejada" valor={`${form.data_desejada}${form.periodo ? ' · ' + form.periodo : ''}`} />
            <p className="text-xs text-gray-500 pt-2">Ao enviar, a solicitação já vai com o valor total de <b>{formatCurrency(totalServicos)}</b>. Em seguida você poderá aprovar o valor para autorizar a execução (a Tenance pode ajustar se necessário).</p>
          </div>
        )}
      </div>

      <div className="flex justify-between mt-4">
        <Button variant="outline" onClick={() => (passo === 0 ? navigate(-1) : setPasso(passo - 1))} data-testid="btn-voltar-passo">
          <ChevronLeft size={16} className="mr-1" /> Voltar
        </Button>
        {passo < 6 ? (
          <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={!podeAvancar()} onClick={() => setPasso(passo + 1)} data-testid="btn-proximo-passo">
            Próximo <ChevronRight size={16} className="ml-1" />
          </Button>
        ) : (
          <Button className="bg-[#2B7A3D] hover:bg-[#236632] h-11 px-6" disabled={enviando} onClick={enviar} data-testid="btn-enviar-solicitacao">
            {enviando ? <Loader2 className="animate-spin" size={18} /> : 'ENVIAR SOLICITAÇÃO'}
          </Button>
        )}
      </div>
    </div>
  );
}

const Resumo = ({ label, valor }) => (
  <div className="flex gap-2 border-b border-gray-100 pb-2">
    <span className="text-gray-500 w-28 shrink-0">{label}</span>
    <span className="font-medium text-[#3D4F5F]">{valor}</span>
  </div>
);
